import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import CroppedImage from './CroppedImage';
import { toast } from 'sonner';
import StepNavigation from './StepNavigation';
import { findProductConfig, getProductRatio, calculateAspectPadding } from '../utils/productUtils';

function OrdersSection({ orders, isAdmin = false, siteTimezone, formatDate, formatTime, formatDateTime, setActiveTab, printSizes = {} }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const viewOrderDetails = async (orderId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/orders/${orderId}`);
      setOrderDetails(response.data);
      setSelectedOrder(orderId);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  // Show order details view
  if (selectedOrder && orderDetails) {
    const order = orderDetails;
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Order Details</h2>
          <Button 
            variant="outline"
            onClick={() => {
              setSelectedOrder(null);
              setOrderDetails(null);
            }}
            data-testid="back-to-orders"
          >
            <i className="pi pi-arrow-left mr-2"></i>
            Back to Orders
          </Button>
        </div>

        {/* Order Summary Card */}
        <Card className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Order #{order.id.slice(-8)}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Placed on {formatDateTime(order.created_at, siteTimezone, { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            </div>
            <Badge 
              variant={order.status === 'pending' ? 'secondary' : 'default'}
              className="text-base px-4 py-2 w-fit"
            >
              {order.status.toUpperCase()}
            </Badge>
          </div>

          {/* Delivery Information */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <i className="pi pi-truck mr-2"></i>
              Delivery Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-600">Shipping Method</p>
                <p className="font-medium capitalize">{order.shipping?.method || 'Standard'}</p>
              </div>
              <div>
                <p className="text-gray-600">Estimated Delivery</p>
                <p className="font-medium">{order.shipping?.delivery_days || '5-7 business days'}</p>
              </div>
              {order.shipping?.address && (
                <div className="sm:col-span-2">
                  <p className="text-gray-600 mb-1">Delivery Address</p>
                  <p className="font-medium">
                    {order.shipping.address.street}<br />
                    {order.shipping.address.city}, {order.shipping.address.state} {order.shipping.address.postcode}<br />
                    {order.shipping.address.country || 'Australia'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-4 text-lg">Order Items ({order.items?.length || 0})</h4>
            <div className="space-y-4">
              {order.items?.map((item, index) => {
                // Get aspect ratio from item's ratio string (e.g., "6:4") or from printSizes
                let aspectRatioPadding = 100; // Default square
                
                // Use shared utility to find product config
                const { config: sizeConfig } = findProductConfig(item, printSizes);
                
                // Get the orientation from the item (saved during checkout)
                const orientation = item.aspectOrientation || 'landscape';
                
                if (item.ratio) {
                  // Use stored ratio (format: "6:4")
                  const [ratioX, ratioY] = item.ratio.split(':').map(parseFloat);
                  if (ratioX && ratioY) {
                    const isNaturallyLandscape = ratioX > ratioY;
                    
                    if (orientation === 'portrait') {
                      // Portrait: taller container
                      aspectRatioPadding = isNaturallyLandscape
                        ? (ratioX / ratioY) * 100  // 6x4 portrait: 6/4 = 150%
                        : (ratioY / ratioX) * 100; // 8x10 portrait: 10/8 = 125%
                    } else {
                      // Landscape: wider container
                      aspectRatioPadding = isNaturallyLandscape
                        ? (ratioY / ratioX) * 100  // 6x4 landscape: 4/6 = 66.67%
                        : (ratioX / ratioY) * 100; // 8x10 landscape: 8/10 = 80%
                    }
                  }
                } else if (sizeConfig?.crop_ratio_x && sizeConfig?.crop_ratio_y) {
                  // Get ratio from printSizes config using shared utility
                  const ratioX = sizeConfig.crop_ratio_x;
                  const ratioY = sizeConfig.crop_ratio_y;
                  const isNaturallyLandscape = ratioX > ratioY;
                  
                  if (orientation === 'portrait') {
                    aspectRatioPadding = isNaturallyLandscape
                      ? (ratioX / ratioY) * 100
                      : (ratioY / ratioX) * 100;
                  } else {
                    aspectRatioPadding = isNaturallyLandscape
                      ? (ratioY / ratioX) * 100
                      : (ratioX / ratioY) * 100;
                  }
                }
                
                console.log(`📐 Order item ${item.name}: sku=${item.sku}, size=${item.size}, ratio=${item.ratio}, orientation=${orientation}, padding=${aspectRatioPadding.toFixed(1)}%`);

                return (
                  <div key={index} className="flex gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    {/* Image Thumbnail */}
                    <div className="w-24 sm:w-32 flex-shrink-0">
                      <div 
                        className="relative overflow-hidden rounded"
                        style={{ 
                          width: '100%',
                          paddingBottom: `${aspectRatioPadding}%` 
                        }}
                      >
                        {/* Show thumbnail if available */}
                        {item.thumbnail && item.thumbnail.length > 100 && (
                          <img 
                            src={`data:image/jpeg;base64,${item.thumbnail}`}
                            alt={item.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => {
                              console.error('❌ Thumbnail failed to load for', item.name);
                            }}
                            onLoad={() => {
                              console.log('✅ Thumbnail loaded for', item.name);
                            }}
                          />
                        )}
                        {/* Fallback to image_data */}
                        {!(item.thumbnail && item.thumbnail.length > 100) && item.image_data && item.image_data.length > 100 && (
                          <img 
                            src={`data:image/jpeg;base64,${item.image_data}`}
                            alt={item.name}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        )}
                        {/* Fallback to backend fetch */}
                        {!(item.thumbnail && item.thumbnail.length > 100) && !(item.image_data && item.image_data.length > 100) && item.image_id && (
                          <img 
                            src={`/api/images/${item.image_id}?thumbnail=true`}
                            alt={item.name}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        )}
                        {/* Show "No Preview" only if no data at all */}
                        {!(item.thumbnail && item.thumbnail.length > 100) && !(item.image_data && item.image_data.length > 100) && !item.image_id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-200 text-gray-400 text-xs">
                            No Preview
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-gray-900 truncate">{item.name}</h5>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-gray-600">Size:</span>
                          <span className="ml-1 font-medium">{item.size}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Paper:</span>
                          <span className="ml-1 font-medium capitalize">{item.paper_type}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Border:</span>
                          <span className="ml-1 font-medium">{item.border ? 'Yes' : 'No'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Quantity:</span>
                          <span className="ml-1 font-medium">{item.quantity}</span>
                        </div>
                      </div>
                    </div>

                    {/* Item Price */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-gray-900">
                        ${(item.total_price / 100).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        ${(item.unit_price / 100).toFixed(2)} each
                      </p>
                      {item.attribute_surcharge > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          (incl. +${(item.attribute_surcharge / 100).toFixed(2)} attributes)
                        </p>
                      )}
                      {item.bulk_discount_applied && (
                        <p className="text-xs text-green-600 mt-0.5">
                          ✓ Bulk discount applied
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pricing Breakdown */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-semibold text-gray-900 mb-3">Pricing Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">${(order.subtotal / 100).toFixed(2)}</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount {order.coupon_code && `(${order.coupon_code})`}</span>
                  <span className="font-medium">-${(order.discount_amount / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">${(order.shipping_cost / 100).toFixed(2)}</span>
              </div>
              {order.tax_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax/GST</span>
                  <span className="font-medium">${(order.tax_amount / 100).toFixed(2)}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-custom-orange">${(order.total_amount / 100).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <i className="pi pi-credit-card mr-2"></i>
              Payment Summary
            </h4>
            <div className="space-y-2 text-sm bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Method</span>
                <span className="font-medium capitalize">
                  {order.payment_method === 'test_mode' ? 'Test Payment' : 
                   order.payment_method === 'stripe' ? 'Stripe (Card)' :
                   order.payment_method === 'square' ? 'Square' :
                   order.payment_method || 'Not specified'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Charged</span>
                <span className="font-medium">${(order.total_amount / 100).toFixed(2)} AUD</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Payment Status</span>
                <Badge 
                  className={
                    order.payment_status === 'paid' ? 'bg-green-500 text-white' :
                    order.payment_status === 'pending' ? 'bg-yellow-500 text-white' :
                    order.payment_status === 'failed' ? 'bg-red-500 text-white' :
                    'bg-gray-500 text-white'
                  }
                >
                  {order.payment_status === 'paid' ? '✓ Paid' :
                   order.payment_status === 'pending' ? '⏳ Pending' :
                   order.payment_status === 'failed' ? '✗ Failed' :
                   order.payment_status || 'Unknown'}
                </Badge>
              </div>
              {order.payment_method === 'test_mode' && (
                <div className="mt-2 p-2 bg-orange-100 rounded text-xs text-orange-700">
                  ⚠️ This order was placed using test payment mode
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Show orders list
  if (orders.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="pi pi-file text-6xl text-gray-400 mx-auto mb-4 block"></i>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No orders yet</h3>
        <p className="text-gray-600">Your order history will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step Navigation */}
      {setActiveTab && (
        <StepNavigation 
          currentStep="orders"
          onStepChange={setActiveTab}
          canGoNext={false}
          canGoBack={true}
        />
      )}
      
      <div className="flex items-center justify-between">
        <div className="text-center flex-1">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Order History</h2>
          <p className="text-gray-600">Track your print orders</p>
        </div>
        {setActiveTab && (
          <Button 
            onClick={() => setActiveTab('upload')}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold"
            data-testid="back-to-upload"
          >
            <i className="pi pi-plus-circle mr-2"></i>
            New Order
          </Button>
        )}
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      )}

      {!loading && (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card 
              key={order.id} 
              className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => viewOrderDetails(order.id)}
              data-testid={`order-${order.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">Order #{order.id.slice(-8)}</h4>
                  <p className="text-sm text-gray-600">
                    {formatDateTime(order.created_at, siteTimezone, { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </p>
                </div>
                <div className="text-right">
                  <Badge variant={order.status === 'pending' ? 'secondary' : 'default'}>
                    {order.status.toUpperCase()}
                  </Badge>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    ${(order.total_amount / 100).toFixed(2)}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Items</p>
                  <p className="font-medium">{order.items?.length || 0} items</p>
                </div>
                <div>
                  <p className="text-gray-600">Shipping</p>
                  <p className="font-medium capitalize">{order.shipping?.method || 'Standard'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Delivery</p>
                  <p className="font-medium">{order.shipping?.delivery_days || '5-7 business days'}</p>
                </div>
              </div>

              <div className="mt-4 text-sm text-custom-orange font-medium flex items-center">
                Click to view details
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrdersSection;
